import React from "react"
import { Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons"
import { motion } from "framer-motion"
import { DateTime } from "luxon"
import { theme } from "../settings/config"
import TextInput from "../inputs/TextInput"
import DateInput from "../inputs/DateInput"

// Il campo costa una cosa; luce e riscaldamento, dove il circolo li fa
// pagare a parte, ne costano un'altra. Si scrive il prezzo pieno di ognuno,
// quello che il circolo pubblica: i rincari l'app se li ricava da sé, e
// quando sono accesi tutti e due li somma.
//
// Compilare "con la luce" o "con il riscaldamento" non è solo questione di
// soldi: è quello che fa comparire l'interruttore quando si scrive una
// lezione. Dove il prezzo non cambia l'interruttore non c'è, perché non
// servirebbe a niente.
const BLOCKS = [
	{
		key: "base",
		title: "Il campo",
		hint: "Quanto ti costa un'ora di campo, senza niente in più.",
	},
	{
		key: "lights",
		title: "Con la luce",
		hint: "Solo se la luce te la fanno pagare: il prezzo pieno dell'ora, luce compresa.",
	},
	{
		key: "heat",
		title: "Con il riscaldamento",
		hint: "Solo se il riscaldamento te lo fanno pagare: il prezzo pieno dell'ora, riscaldamento compreso.",
	},
]

// `noHeat` è il vecchio nome del prezzo base: le tariffe scritte allora si
// leggono come sono, e si riscrivono col nome nuovo appena si tocca qualcosa.
const readSportFees = sportFees => {
	if (!sportFees) return {}
	const { noHeat, ...rest } = sportFees
	if (noHeat === undefined) return rest

	return { ...rest, base: rest.base ?? noHeat }
}

// Quanto costa il campo a te. Ogni tariffa vale dalla sua data in avanti,
// finché non ne comincia una nuova: così il riepilogo di ottobre usa il
// prezzo di ottobre anche se nel frattempo è aumentato.
function CourtsFeeEditor({ sportID, fees, isEditing, onChange }) {
	const sportFees = readSportFees(fees[sportID])

	const setTable = (blockKey, table) => {
		const nextSportFees = { ...sportFees }

		if (Object.keys(table).length === 0) delete nextSportFees[blockKey]
		else nextSportFees[blockKey] = table

		const next = { ...fees }
		if (Object.keys(nextSportFees).length === 0) delete next[sportID]
		else next[sportID] = nextSportFees

		onChange(next)
	}

	const addFee = blockKey => {
		const table = { ...(sportFees[blockKey] ?? {}) }
		const today = DateTime.now().toISODate()
		if (table[today] !== undefined) return
		setTable(blockKey, { ...table, [today]: 0 })
	}

	const removeFee = (blockKey, from) => {
		const table = { ...(sportFees[blockKey] ?? {}) }
		delete table[from]
		setTable(blockKey, table)
	}

	// La data è la chiave della tabella: cambiarla vuol dire spostare la
	// riga, non modificarla.
	const moveFee = (blockKey, from, nextFrom) => {
		if (!nextFrom || nextFrom === from) return
		const table = { ...(sportFees[blockKey] ?? {}) }
		if (table[nextFrom] !== undefined) return
		table[nextFrom] = table[from]
		delete table[from]
		setTable(blockKey, table)
	}

	const setFee = (blockKey, from, raw) => {
		const table = { ...(sportFees[blockKey] ?? {}) }
		table[from] = raw === "" ? 0 : Number(raw)
		setTable(blockKey, table)
	}

	if (!sportID) return null

	return (
		<>
			{BLOCKS.map(block => {
				const table = sportFees[block.key] ?? {}
				const dates = Object.keys(table).sort()

				if (!isEditing && dates.length === 0) return null

				return (
					<Div key={block.key} m={{ b: "1rem" }}>
						<Text
							textSize="body"
							textWeight="bold"
							textColor={theme.colors.text_light}>
							{block.title}
						</Text>
						{isEditing && (
							<Text
								textSize="caption"
								textColor={theme.colors.text_light}
								m={{ b: "0.5rem" }}>
								{block.hint}
							</Text>
						)}

						{dates.map(from => (
							<Div
								key={from}
								d="flex"
								align="center"
								m={{ b: "0.5rem" }}
								style={{ gap: "0.5rem" }}>
								<Text
									textSize="body"
									textColor={theme.colors.text_light}
									style={{ flexShrink: 0 }}>
									dal
								</Text>

								{isEditing ? (
									<>
										<Div style={{ flex: 1 }}>
											<DateInput
												value={from}
												m="0"
												handleChange={nextFrom =>
													moveFee(block.key, from, nextFrom)
												}
											/>
										</Div>
										<Div w="6.5rem" style={{ flexShrink: 0 }}>
											<TextInput
												type="number"
												value={table[from]}
												m="0"
												textAlign="right"
												p={{ l: "0.75rem", r: "2.75rem" }}
												handleChange={e =>
													setFee(block.key, from, e.target.value)
												}
												suffix={
													<Div
														pos="absolute"
														top="0.9rem"
														right="0.6rem"
														bg="transparent">
														<Text
															textSize="caption"
															textColor={theme.colors.text_light}>
															€/h
														</Text>
													</Div>
												}
											/>
										</Div>
										<motion.div whileTap={{ scale: 0.9 }}>
											<Div
												cursor="pointer"
												p="0.25rem 0.5rem"
												onClick={() => removeFee(block.key, from)}>
												<FontAwesomeIcon
													icon={faXmark}
													color={theme.colors.text_light}
												/>
											</Div>
										</motion.div>
									</>
								) : (
									<Text textSize="body">
										{DateTime.fromISO(from).toFormat("dd/MM/yyyy")}:{" "}
										<Text tag="span" textWeight="bold">
											{table[from]} €/h
										</Text>
									</Text>
								)}
							</Div>
						))}

						{isEditing && (
							<Div
								d="flex"
								align="center"
								cursor="pointer"
								style={{ gap: "0.5rem" }}
								onClick={() => addFee(block.key)}>
								<FontAwesomeIcon icon={faPlus} color={theme.colors.primary} />
								<Text textSize="body" textColor={theme.colors.primary}>
									{dates.length === 0 ? "Aggiungi prezzo" : "Aggiungi tariffa"}
								</Text>
							</Div>
						)}
					</Div>
				)
			})}

			{isEditing && (
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
					Lascia vuoto se il campo non lo paghi tu: senza tariffe non viene scalato
					niente dal riepilogo.
				</Text>
			)}
		</>
	)
}

export default CourtsFeeEditor
